import { ColDef, CsvExportParams, ExcelExportParams, ProcessCellForExportParams } from 'ag-grid-community';
import { get, merge } from 'lodash-es';
import { useMemo } from 'react';
import { RendererType } from '../../data-models/field-definition';
import { getPlainDateString } from '../../services/queries/MaggieMetricsQueries';
import {
  ExcelFormatterIdPrefix,
  getCompactExcelStyles,
  getExcelAgFormat,
  getExcelDivisorForColDef,
  getExcelFormatterId,
} from '../../util/excel-formatter-service';
import { DateFormattersId, StandardFormatterId } from '@foresightdata/web-common-core';
import { FMT } from '@foresightdata/web-common-formatter-service';
import { isObject } from '../../util/object-util';
import { isTreeNodeRow } from './cell-renderers/GenericIdRenderer';
import { sanitizeAsFileName } from '../../util/stringUtils';

/** `ProcessCellForExportParams.type` is typed as a bare string; ag-grid passes this for Excel export. */
const ExcelExportType = 'excel';

export function commonProcessCb(params: ProcessCellForExportParams) {
  const colDef = params.column.getColDef();
  const { cellDataType } = colDef;
  const val = params.node?.group === true && isObject(params.value) ? params.value?.value : params.value;

  // If there is an Excel cell class then excelStyles will be applied to the cell
  if (hasExcelStyleClass(colDef)) {
    // Only Excel: the same callback serves CSV, which has no number format to pair the scaling with and
    // would show a bare fraction where it shows a whole percentage today.
    const divisor = params.type === ExcelExportType ? getExcelDivisorForColDef(colDef) : 1;

    return divisor !== 1 && typeof val === 'number' ? val / divisor : val;
  }

  if (cellDataType === 'date' || cellDataType === 'dateString') {
    // Return the raw date from the BE which will be in ISO date format YYYY-MM-DD
    return get(params.node?.data, (params.column?.getDefinition() as ColDef)?.field ?? '') ?? '';
  }

  // Use Foresight value formatting
  return params.formatValue(val);
}

/**
 * Whether excelStyles will supply this column's number format, in which case the raw value has to be
 * exported rather than the formatted string. Rule keys are checked too, since per-cell magnitude
 * styles are declared there; several call sites also push a possibly-null id into cellClass, so the
 * entries are type-checked rather than assumed to be strings.
 */
function hasExcelStyleClass({ cellClass, cellClassRules }: ColDef): boolean {
  const isExcelStyleId = (value: unknown) =>
    typeof value === 'string' && value.startsWith(ExcelFormatterIdPrefix);

  return (
    (Array.isArray(cellClass) && cellClass.some(isExcelStyleId)) ||
    Object.keys(cellClassRules ?? {}).some(isExcelStyleId)
  );
}

export function groupAwareProcessCb(params: ProcessCellForExportParams) {
  // A tree node row's key is the path key, not a group value — it must export its cell instead.
  if (params.node?.group && params.column.getColId() === 'ag-Grid-AutoColumn' && !isTreeNodeRow(params)) {
    return params.node.key ?? '';
  }
  return commonProcessCb(params);
}

export function getExportToExcelStyles() {
  const numberOrDateFormatters = FMT.get()
    .getAllFormatterModels()
    .filter((f) => f.type === 'number' || f.type === 'date');

  return [
    ...numberOrDateFormatters.map((f) => getExcelAgFormat(f)).filter((excelStyle) => excelStyle !== null),
    // Registered last on purpose: when two styles match a cell ag-grid deep-merges them in this
    // order, so the per-cell magnitude style wins over the formatter's base style.
    ...numberOrDateFormatters.flatMap((f) => getCompactExcelStyles(f)),
  ];
}

export function getRendererTypeToExcelStyleId(rendererType: RendererType) {
  switch (rendererType) {
    case RendererType.number:
      return getExcelFormatterId('numeric2DP')!;
    case RendererType.currency:
      return getExcelFormatterId('currency')!;
    case RendererType.text:
      return getExcelFormatterId('string')!;
    default:
      return getExcelFormatterId(rendererType as StandardFormatterId)!;
  }
}

export function getFormatterIdToExcelStyleId(formatterId: StandardFormatterId | string) {
  switch (formatterId) {
    case 'date':
      return getExcelFormatterId('date')!;
    case 'integer':
      return getExcelFormatterId('integer')!;
    case 'multiplier':
      return getExcelFormatterId('multiplier')!;
    case 'naturalNumber':
      return getExcelFormatterId('naturalNumber')!;
    case 'number':
      return getExcelFormatterId('numeric2DP')!;
    case 'percent2dpAsIs':
    case 'percent':
      return getExcelFormatterId('percent')!;
    case 'percent2dpAsIsPositive':
      return getExcelFormatterId('percent2dpAsIsPositive' as StandardFormatterId)!;
    // Each keeps its own style rather than collapsing onto `percent`: they differ in decimal count and
    // in whether the raw value needs scaling, and the default branch below would leave them without a
    // numeric style at all, exporting a text cell.
    case 'decimalPercent':
    case 'percent0Dp':
    case 'percent2DpShort':
    case 'percentHighPrecision':
      return getExcelFormatterId(formatterId as StandardFormatterId)!;
    case 'usd':
    case 'usdShort':
    case 'currency':
    case 'currencyShort':
    case 'currency0Dp':
      return getExcelFormatterId('currency')!;
    default:
      return getExcelFormatterId('string')!;
  }
}

/**
 *
 * @param colIds set of column ids (`column.colId`) that should be exported with custom formatting,
 * by calling the defined valueFormatter for each column
 * @param fileName
 * @returns  export params that can be passed as defaultExcelExportParams or defaultCsvExportParams
 */
export function useExtendedExportSettings<T>(colIds: Set<string>, fileName?: string) {
  const exportParams: ExcelExportParams & CsvExportParams = useMemo(() => {
    function processCb(params: ProcessCellForExportParams<T>) {
      const colId = params.column.getColId();
      if (colIds.has(colId)) {
        return params.formatValue(params.value);
      }
      return commonProcessCb(params);
    }

    return {
      processCellCallback: processCb,
      fileName,
    };
  }, [colIds, fileName]);

  return { exportParams };
}

export function getExportParams(params: Partial<ExcelExportParams | CsvExportParams> = {}) {
  let fileName = params?.fileName;
  if (fileName && typeof fileName === 'string') {
    fileName = sanitizeAsFileName(fileName!);
    return merge({}, params, { fileName });
  }
  return params;
}

function getDateFormatters() {
  return new Set<string>(Object.values(DateFormattersId));
}
export function processValueForExport(value: unknown, format?: StandardFormatterId | string) {
  const dateFormatters = getDateFormatters();
  switch (true) {
    case dateFormatters.has(format ?? '') && value instanceof Date: {
      return getPlainDateString(value);
    }
    case format?.startsWith('percent') && typeof value === 'number': {
      return value / 100;
    }

    default:
      return value;
  }
}
